import React, { useEffect, useRef } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useOrderForm } from '../contexts/OrderFormContext';

interface PricingEstimateProps {
  brand: BrandConfig;
  county: string;
  priority: string;
}

const PricingEstimate: React.FC<PricingEstimateProps> = ({ brand, county, priority }) => {
  const { estimatedPrice, isPricingLoading, fetchPricing } = useOrderForm();
  const prevCountyRef = useRef(county);
  const prevPriorityRef = useRef(priority);

  useEffect(() => {
    if (county !== prevCountyRef.current || priority !== prevPriorityRef.current) {
      prevCountyRef.current = county;
      prevPriorityRef.current = priority;
      fetchPricing(county, priority);
    }
  }, [county, priority, fetchPricing]);

  // Fetch on mount if county is already set
  useEffect(() => {
    if (county) {
      fetchPricing(county, priority);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="rounded-lg border p-3 mt-3"
      style={{
        borderColor: brand.colors.border,
        backgroundColor: brand.colors.bgAccent,
      }}
    >
      <p className="text-xs font-semibold mb-1" style={{ color: brand.colors.textLight }}>
        Estimated Service Fee
      </p>
      {isPricingLoading ? (
        <div className="flex items-center gap-2">
          <div
            className="h-4 w-4 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
          />
          <span className="text-sm" style={{ color: brand.colors.textLight }}>
            Calculating...
          </span>
        </div>
      ) : estimatedPrice ? (
        <p className="text-lg font-bold" style={{ color: brand.colors.link }}>
          ${estimatedPrice.serviceFee.toFixed(2)}
        </p>
      ) : (
        <p className="text-sm" style={{ color: brand.colors.textMuted }}>
          &mdash;
        </p>
      )}
    </div>
  );
};

export default PricingEstimate;
